<script>
  /** @type {{
    id?: string,
    type?: string,
    value?: string?,
    checked?: boolean,
    placeholder?: string,
    label?: string | Svelte.Snippet,
    output?: string | Svelte.Snippet<[{ id: string, value?: string? }]>,
    action?: import('svelte/action').Action,
    class?: string,
    [key: string]: any
  }} */
  let {
    id = `input-${Math.random().toString(32).substring(2)}`,
    type = 'text',
    placeholder,
    label,
    output,
    action = () => {},
    value = $bindable(),
    checked = $bindable(),
    ...rest
  } = $props()

  let attrs = $derived({
    ...rest,
    id,
    type,
    placeholder,
    class: `field ${type} ${rest.class ?? ''}`
  })
</script>

<label
  for={id}
  class="input"
  class:toggle={type === 'checkbox' || type === 'radio'}>
  {#if label || placeholder}
    <span class="label" class:u-hidden={!label}>
      {#if typeof label === 'function'}
        {@render label()}
      {:else}
        {label || placeholder}
      {/if}
    </span>
  {/if}
  <span class="container">
    {#if type === 'checkbox'}
      <input {...attrs} type="checkbox" bind:checked use:action />
    {:else}
      <input {...attrs} bind:value use:action />
      {#if type !== 'radio' && output}
        <span class="output">
          {#if typeof output === 'function'}
            {@render output({ id, value })}
          {:else}
            {output}
          {/if}
        </span>
      {/if}
    {/if}
  </span>
</label>

<style>
  .input {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    position: relative;
  }

  .toggle {
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 0.75rem;
    cursor: pointer;
    transition: var(--color-transition);
    user-select: none;
  }

  .label {
    display: inline-block;
    flex: 1 1 0;
    font-weight: 550;
    line-height: 1.2;
    font-size: 1rem;
    text-wrap: balance;
  }

  .toggle .label {
    font-weight: normal;
    white-space: normal;
    line-height: inherit;
  }

  .container {
    position: relative;
  }

  .field {
    display: block;
    flex: 1 1 auto;
    width: 100%;
    height: auto;
    padding: 0.7rem 1rem;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    border-radius: var(--border-radius-sm);
    background: var(--background);
    color: rgb(var(--rgb-text));
    font-size: 1rem;
    text-overflow: ellipsis;
    appearance: none;
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
    resize: none;
  }

  .field:disabled {
    color: rgb(var(--rgb-muted));
  }

  :is(.field):not(:disabled):hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .field:active {
    box-shadow: var(--box-shadow-sm-active);
  }

  .field:focus {
    border-color: rgba(var(--rgb-muted), 0.5);
    box-shadow: var(--box-shadow-sm-hover);
  }

  .toggle .field {
    flex: 0 0 auto;
    width: 1.25rem;
    height: 1.25rem;
    padding: 0;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    cursor: pointer;
    appearance: none;
    background: var(--background);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
  }

  .checkbox {
    border-radius: var(--border-radius-xs);
  }

  .radio {
    border-radius: 100%;
  }

  :is(.checkbox, .radio):hover {
    box-shadow: var(--box-shadow-sm-hover);
    border-color: rgba(var(--rgb-gray), 0.5);
  }

  :is(.checkbox, .radio):active {
    box-shadow: var(--box-shadow-sm-active);
  }

  .checkbox:checked {
    background-image: url('$lib/assets/checkmark.svg');
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: 0 2px;
  }

  .radio:checked {
    background-image: url('$lib/assets/circle.svg');
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: center;
  }

  .checkbox:focus {
    border-color: rgba(var(--rgb-muted), 0.5);
    box-shadow: var(--box-shadow-sm-hover);
  }

  .output {
    display: flex;
    align-items: center;
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.8rem;
    color: rgb(var(--rgb-muted));
    pointer-events: none;
  }
</style>
